import React from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/layout/PageTransition';
import Reveal from '../components/ui/Reveal';
import SectionHeader from '../components/ui/SectionHeader';
import CTABanner from '../components/sections/CTABanner';
import Process from '../components/sections/Process';
import { TEAM, VALUES, COMPANY } from '../data/site';

export default function AboutPage() {
  return (
    <PageTransition>
      <section className="page-hero">
        <div className="container">
          <Reveal><span className="eyebrow"><span className="dot" />About Voila Tech</span></Reveal>
          <Reveal delay={0.06} as="h1">
            A senior studio for founders who care about the difference between built and <span className="serif gold">built-well</span>.
          </Reveal>
          <Reveal delay={0.12}>
            <p className="lede">
              Voila Tech is a small, deliberate team. We work on a handful of products at a time so
              everyone we hire stays close to the work. No subcontractors, no outsourcing, no
              juniors learning on your dime.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section--tight">
        <div className="container">
          <div className="stat-grid-4">
            {[
              { num: '7',   label: 'Senior team' },
              { num: '40+', label: 'Products shipped' },
              { num: '2',   label: 'Studio locations' },
              { num: '5y',  label: 'Median experience' },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06}>
                <div className="stat-cell">
                  <div className="stat-num">{s.num}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 'calc(var(--section-y) * 0.25)' }}>
        <div className="container">
          <SectionHeader
            eyebrow="Values"
            title="Four things we won't compromise on."
            lede="Most studios drift toward the kind of work that pays the bills. We try not to. These are the constraints that keep us honest."
          />
          <div className="values-grid">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                className="value"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 0.84, 0.44, 1] }}
              >
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--gold)', letterSpacing: '0.12em' }}>{String(i + 1).padStart(2, '0')}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeader
            eyebrow="Team"
            title="Six people. Senior across the board."
            lede="The whole team, in one place. No bench, no juniors learning on your dime — just the people who'll actually do your work."
          />
          <div className="team-grid">
            {TEAM.map((m, i) => (
              <motion.div
                key={m.name}
                className="team-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 0.84, 0.44, 1] }}
              >
                <div className="team-card__avatar">{m.initials}</div>
                <div className="team-card__name">{m.name}</div>
                <div className="team-card__role">{m.role}</div>
                <div className="team-card__bio">{m.bio}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Process />

      <section className="section">
        <div className="container">
          <SectionHeader
            eyebrow="Where we work"
            title="Two studios, one team."
            lede="We split between Bengaluru and Chennai, with most engagements running on overlapping IST hours. We've worked with clients across PT, ET, and CET — async by default, deep work in the morning, sync calls in the afternoon."
          />
          <div className="studios-grid">
            {COMPANY.locations.map((loc, i) => (
              <Reveal key={loc} delay={i * 0.08}>
                <div className="studio-card">
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Studio · 0{i + 1}</div>
                  <h3 style={{ marginTop: 12, fontSize: 28 }}>{loc}</h3>
                  <p style={{ color: 'var(--muted)', marginTop: 12 }}>India Standard Time · IST</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </PageTransition>
  );
}
